import React, { useState, useEffect, useCallback } from "react";
import { MdClose } from "react-icons/md";
import { Button } from "../../ui/UiComponents";
import { useTranslation } from "react-i18next";

const Dialog = ({
  isOpen = false,
  onClose,
  title = "",
  text = "",
  metaText = "",
  image = null,
  buttons = [],
  children = null,
  showOverlay = true,
  autoClose = false,
  autoCloseDelay = 5000,
  closeButton = true,
  delay = 0,
  className = "",
  titleClassName = "",
  textClassName = "",
  metaTextClassName = "",
  buttonsClassName = "",
  overlayClassName = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const { t } = useTranslation();
  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      if (onClose) onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      if (delay > 0) {
        const showTimer = setTimeout(() => {
          setShouldRender(true);
          setTimeout(() => setIsVisible(true), 10);
        }, delay);
        return () => clearTimeout(showTimer);
      } else {
        setShouldRender(true);
        setTimeout(() => setIsVisible(true), 10);
      }
    } else {
      setIsVisible(false);
      const hideTimer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(hideTimer);
    }
  }, [isOpen, delay]);

  useEffect(() => {
    if (autoClose && isVisible && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoClose, isVisible, autoCloseDelay, handleClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleButtonClick = (button) => {
    if (button.action) {
      button.action();
    }
    if (button.closeOnClick !== false) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0"
      } ${showOverlay ? "bg-black bg-opacity-50" : ""} ${overlayClassName}`}
      onClick={showOverlay ? handleOverlayClick : undefined}
    >
      <div
        className={`bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all duration-300 ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        } ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {closeButton && (
          <div className="flex justify-start items-center p-4 border-b border-gray-200">
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label={t("general.buttons.close")}
            >
              <MdClose className="w-6 h-6" />
            </button>
          </div>
        )}

        <div className="p-6 text-center">
          {image && (
            <div className="mb-4">
              {typeof image === "string" ? (
                <img
                  src={image}
                  alt=""
                  className="w-16 h-16 mx-auto object-contain"
                />
              ) : (
                <div className="w-16 h-16 mx-auto flex items-center justify-center">
                  {image}
                </div>
              )}
            </div>
          )}

          {title && (
            <h3
              className={`text-lg font-semibold text-gray-900 mb-3 ${titleClassName}`}
            >
              {title}
            </h3>
          )}

          {text && (
            <p className={`text-gray-700 mb-3 ${textClassName}`}>{text}</p>
          )}

          {metaText && (
            <p className={`text-sm text-gray-500 mb-4 ${metaTextClassName}`}>
              {metaText}
            </p>
          )}

          {children && <div className="mb-4 text-start">{children}</div>}

          {buttons.length > 0 && (
            <div
              className={`flex flex-col sm:flex-row sm:space-y-0 sm:space-x-3 sm:justify-center gap-2 flex-wrap ${buttonsClassName}`}
            >
              {buttons.map((button, index) => (
                <Button
                  key={index}
                  onClick={() => handleButtonClick(button)}
                  disabled={button.disabled}
                  variant={button.variant}
                  className={button.className}
                  size="sm"
                >
                  {button.text}
                </Button>
              ))}
            </div>
          )}
        </div>

        {autoClose && isVisible && autoCloseDelay > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all ease-linear"
              style={{
                animation: `shrink ${autoCloseDelay}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export default Dialog;
